/**
 * ShiftDetailsModal
 * -----------------
 * Read-only details view for a single shift, opened when a staff member
 * clicks a shift card on their schedule.
 *
 * No backend changes — consumes the existing ScheduleResponse payload and
 * an optional restaurant object (location_name, address, etc.) already
 * available through restaurantsAPI.getOne.
 *
 * Future-ready: includes a disabled Clock In shortcut placeholder so the
 * dashboard clock card can wire into it later without restructuring.
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Calendar,
  Clock,
  MapPin,
  Coffee,
  User,
  Phone,
  FileText,
  AlertCircle,
  ShieldCheck,
  ExternalLink,
  Repeat,
} from 'lucide-react';

function formatTime(t) {
  if (!t) return '—';
  // accepts "HH:MM" or "HH:MM:SS"
  return t.length >= 5 ? t.slice(0, 5) : t;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function fullAddress(r) {
  if (!r) return null;
  return [r.address, r.city, r.state, r.postal_code]
    .filter(Boolean)
    .join(', ');
}

const Row = ({ icon: Icon, label, value, children }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-b-0">
    <Icon size={18} className="text-slate-400 mt-0.5 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
      {value !== undefined ? (
        <p className="text-sm text-slate-900 mt-0.5 break-words">{value || '—'}</p>
      ) : (
        children
      )}
    </div>
  </div>
);

export default function ShiftDetailsModal({
  open,
  onClose,
  shift,
  restaurant,
  supervisor,
  canRequestReplacement = false,
  replacementRequested = false,
  onRequestReplacement,
}) {
  if (!shift) return null;

  const address = fullAddress(restaurant);
  const mapsQuery = address || restaurant?.location_name || '';
  const mapsUrl = mapsQuery
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`
    : null;

  const title =
    shift.template_name ||
    shift.role_name ||
    `Shift on ${formatDate(shift.date)}`;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="shift-details-modal"
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: shift.role_color || '#FF7A1A', color: '#fff' }}
            >
              <Calendar size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle
                className="text-lg font-semibold text-slate-900 break-words"
                data-testid="shift-details-title"
              >
                {title}
              </DialogTitle>
              <DialogDescription className="text-sm text-slate-500 mt-0.5">
                {formatDate(shift.date)}
              </DialogDescription>
            </div>
            {shift.status && (
              <Badge
                variant="secondary"
                className={
                  shift.status === 'published'
                    ? 'bg-green-100 text-green-700 hover:bg-green-100'
                    : 'bg-amber-100 text-amber-700 hover:bg-amber-100'
                }
                data-testid="shift-details-status"
              >
                {shift.status}
              </Badge>
            )}
          </div>
          {replacementRequested && (
            <div className="mt-3">
              <Badge
                variant="secondary"
                className="bg-orange-100 text-orange-700 hover:bg-orange-100 border-orange-200"
                data-testid="shift-details-replacement-badge"
              >
                Replacement Requested
              </Badge>
            </div>
          )}
        </DialogHeader>

        <div className="mt-4 space-y-0">
          {/* Property */}
          <Row
            icon={MapPin}
            label="Property"
            value={restaurant?.location_name || '—'}
          />

          {/* Address */}
          <Row icon={MapPin} label="Address">
            {address ? (
              <div className="flex items-center gap-2">
                <p className="text-sm text-slate-900" data-testid="shift-details-address">
                  {address}
                </p>
                {mapsUrl && (
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-medium text-orange-600 hover:text-orange-700 inline-flex items-center gap-1"
                    data-testid="shift-details-maps-link"
                  >
                    Maps <ExternalLink size={12} />
                  </a>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-400">—</p>
            )}
          </Row>

          {/* Time block */}
          <Row icon={Clock} label="Clock In">
            <p className="text-sm text-slate-900" data-testid="shift-details-start">
              {formatTime(shift.start_time)}
            </p>
          </Row>
          <Row icon={Clock} label="Clock Out">
            <p className="text-sm text-slate-900" data-testid="shift-details-end">
              {formatTime(shift.end_time)}
            </p>
          </Row>
          <Row
            icon={Coffee}
            label="Break"
            value={
              shift.break_duration != null && shift.break_duration > 0
                ? `${shift.break_duration} minutes`
                : 'No break'
            }
          />

          {/* Role */}
          {shift.role_name && (
            <Row icon={ShieldCheck} label="Role" value={shift.role_name} />
          )}

          {/* Supervisor */}
          <Row icon={User} label="Supervisor" value={supervisor?.name || '—'} />
          {supervisor?.phone && (
            <Row icon={Phone} label="Supervisor Phone">
              <a
                href={`tel:${supervisor.phone}`}
                className="text-sm text-orange-600 hover:text-orange-700"
                data-testid="shift-details-supervisor-phone"
              >
                {supervisor.phone}
              </a>
            </Row>
          )}

          {/* Notes / Instructions */}
          {shift.notes && (
            <Row icon={FileText} label="Site Notes / Instructions">
              <p
                className="text-sm text-slate-700 whitespace-pre-wrap mt-0.5"
                data-testid="shift-details-notes"
              >
                {shift.notes}
              </p>
            </Row>
          )}

          {/* GPS clock radius status — informational, future-ready */}
          {restaurant?.gps_clock_in_enabled && (
            <Row icon={ShieldCheck} label="Clock-in radius">
              <p className="text-sm text-slate-700">
                GPS verification required — must be within{' '}
                {restaurant?.gps_clock_in_radius_meters || 100}m of the property.
              </p>
            </Row>
          )}

          {/* Emergency contact placeholder if restaurant exposes it */}
          {restaurant?.emergency_contact && (
            <Row icon={AlertCircle} label="Emergency Contact" value={restaurant.emergency_contact} />
          )}
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2 mt-2">
          {canRequestReplacement && !replacementRequested && (
            <Button
              type="button"
              variant="outline"
              onClick={() => onRequestReplacement?.(shift)}
              data-testid="request-replacement-btn"
              className="w-full sm:w-auto border-orange-200 text-orange-700 hover:bg-orange-50"
            >
              <Repeat size={16} className="mr-1.5" />
              Request Replacement
            </Button>
          )}
          {mapsUrl && (
            <Button
              type="button"
              variant="outline"
              onClick={() => window.open(mapsUrl, '_blank', 'noopener,noreferrer')}
              data-testid="shift-details-open-maps-btn"
              className="w-full sm:w-auto"
            >
              <MapPin size={16} className="mr-1.5" />
              Open Maps
            </Button>
          )}
          <Button
            type="button"
            onClick={onClose}
            data-testid="shift-details-close-btn"
            className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
